import {
    Action,
    AnyAction,
    configureStore,
    Middleware,
    Reducer,
    StoreEnhancer,
    SerializableStateInvariantMiddlewareOptions,
} from '@reduxjs/toolkit'
import {createBrowserHistory} from 'history'
import testing from 'redux-testing'

import {isLocalProfile, isRealProfile, isStoryBookProfile, isTestProfile} from '@web/core/config'

import {createSentryEnhancer, EnhancerOptions} from './enhancers'
import {routerMiddleware} from './history'
import {logger} from './middlewares'

export const browserHistory = createBrowserHistory()

// type NoInfer<T> = [T][T extends unknown ? 0 : never]

type Parameters<S, A extends Action = AnyAction> = {
    defaultMiddlewareOptions?: {
        serializableCheck: boolean | SerializableStateInvariantMiddlewareOptions
    }
    rootReducerCreator: () => Reducer<S, A>
    middlewares?: Middleware[]
    preloadedState?: S
    sentryEnhancerOptions?: EnhancerOptions
}

export const createStore = <S, A extends Action = AnyAction>({
    defaultMiddlewareOptions,
    rootReducerCreator,
    middlewares = [],
    preloadedState,
    sentryEnhancerOptions,
}: Parameters<S, A>) => {
    const store = configureStore({
        reducer: rootReducerCreator(),
        devTools: !isRealProfile,
        preloadedState,
        middleware: (getDefaultMiddleware) => {
            return getDefaultMiddleware(defaultMiddlewareOptions).concat([
                routerMiddleware,
                ...middlewares,
                ...(isLocalProfile || isStoryBookProfile ? [logger] : []),
            ])
        },
        enhancers: (getDefaultEnhancers) =>
            getDefaultEnhancers({
                autoBatch: {type: 'tick'},
            }).concat([
                createSentryEnhancer(sentryEnhancerOptions),
                ...(isTestProfile ? [testing as StoreEnhancer] : []),
            ]),
    })

    if (isLocalProfile) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(window as any).store = store

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(module as any).hot?.accept(() => store.replaceReducer(rootReducerCreator()))
    }

    return store
}

export * from './makeReduxHook'
